/**
 * ValidationXII.js
 * Validation rules for HSC Admission Application Form (XI to XII)
 */

// ==================== HELPER VALIDATORS ====================

const isTamilOnly = (text) => {
  if (!text) return false;
  const tamilRegex = /^[\u0B80-\u0BFF\s.,\-()0-9]+$/;
  return tamilRegex.test(text.trim());
};

export const isValidEmail = (email) => {
  const trimmed = email.trim();
  if (/[^a-zA-Z0-9.@]/.test(trimmed)) {
    return {
      valid: false,
      message:
        "Sorry, only letters (a-z), numbers (0-9), and periods (.) are allowed",
    };
  }
  if (/\.@/.test(trimmed)) {
    return {
      valid: false,
      message:
        "Sorry, the last character before @ must be a letter (a-z) or number (0-9)",
    };
  }
  if (!/^[a-zA-Z0-9.]+@[a-zA-Z0-9.]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
    return { valid: false, message: "Please enter a valid email address" };
  }
  return { valid: true, message: "" };
};

const isValidMobile = (mobile) => /^[6-9]\d{9}$/.test(mobile);

const isValidAadhar = (aadhar) => /^\d{12}$/.test(aadhar);

const isValidPincode = (pincode) => /^\d{6}$/.test(pincode);

const isValidEmisNo = (emis) => {
  if (!emis) return false;
  return /^\d{1,10}$/.test(emis);
};

const isValidMark = (mark) => {
  if (mark === "" || mark === null || mark === undefined) return false;
  const num = parseInt(mark);
  return !isNaN(num) && num >= 0 && num <= 100;
};

const getVal = (field) => {
  if (!field) return null;
  if (typeof field === "object" && field.value !== undefined)
    return field.value;
  return field;
};

// ==================== MAIN VALIDATION FUNCTION ====================
export const validateHSCAdmissionForm = (formData, translations) => {
  const errors = {};
  const t = translations?.labels || {};

  // ---- Basic Details ----
  if (!formData.applicant_name_in_tamil?.trim()) {
    errors.applicant_name_in_tamil =
      t?.studentNameTamilRequired || "Student's Name (Tamil) is required";
  } else if (!isTamilOnly(formData.applicant_name_in_tamil)) {
    errors.applicant_name_in_tamil =
      t?.onlyTamilAllowed || "Please enter only Tamil characters";
  }

  if (!formData.applicant_name?.trim()) {
    errors.applicant_name =
      t?.studentNameEnglishRequired || "Student's Name (English) is required";
  }

  if (!formData.date_of_birth) {
    errors.date_of_birth =
      t?.dateOfBirthRequired || "Date of birth is required";
  }
  // else {
  //   const today = new Date();
  //   const dob = new Date(formData.date_of_birth);

  //   let age = today.getFullYear() - dob.getFullYear();
  //   const monthDiff = today.getMonth() - dob.getMonth();

  //   if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
  //     age--;
  //   }

  //   if (age < 15) {
  //     errors.date_of_birth =
  //       t?.dateOfBirthMinAge || "Age must be at least 15 years";
  //   }
  // }

  if (!formData.email?.trim()) {
    errors.email = t?.emailRequired || "Email is required";
  } else {
    const emailResult = isValidEmail(formData.email);
    if (!emailResult.valid) errors.email = emailResult.message;
  }

  if (!formData.mobile_number?.trim()) {
    errors.mobile_number =
      t?.mobileNumberRequired || "Mobile number is required";
  } else if (!isValidMobile(formData.mobile_number)) {
    errors.mobile_number =
      t?.mobileNumberInvalid || "Enter valid 10-digit mobile number";
  }

  if (!formData.aadhar_no) {
    errors.aadhar_no = t?.aadharNumberRequired || "Aadhar number is required";
  }

  if (formData.aadhar_no && !isValidAadhar(formData.aadhar_no)) {
    errors.aadhar_no =
      t?.aadharNumberInvalid || "Enter valid 12-digit Aadhar number";
  }

  if (!getVal(formData.gender)) {
    errors.gender = t?.genderRequired || "Gender is required";
  }

  if (!getVal(formData.religion)) {
    errors.religion = t?.religionRequired || "Religion is required";
  }

  if (!formData.caste?.trim()) {
    errors.caste = t?.casteRequired || "Caste is required";
  }

  if (!getVal(formData.community)) {
    errors.community = t?.categoryRequired || "Category is required";
  }

  if (!formData.nationality?.trim()) {
    errors.nationality = t?.nationalityRequired || "Nationality is required";
  }

  if (!getVal(formData.student_native_state)) {
    errors.student_native_state = t?.stateRequired || "State is required";
  }

  if (!formData.place_of_birth?.trim()) {
    errors.place_of_birth = t?.birthPlaceRequired || "Birth Place is required";
  }

  if (!formData.community_certificate_path) {
    errors.community_certificate_path =
      t.markSheetRequired || "Mark Sheet is required";
    // t?.communityCertificateRequired || "Mark Sheet is required";
  }

  // ---- Residential Details ----
  const livingWith = getVal(formData.living_with);

  if (!livingWith) {
    errors.living_with = t?.livingWithRequired || "This field is required";
  }

  if (livingWith === "parent") {
    if (!formData.parent_name?.trim())
      errors.parent_name = t?.parentNameRequired || "Name is required";
    if (!getVal(formData.parent_occupation))
      errors.parent_occupation =
        t?.occupationRequired || "Occupation is required";
    if (!formData.parent_annual_salary?.toString().trim())
      errors.parent_annual_salary =
        t?.incomeRequired || "Annual Income is required";
    if (!formData.parent_address?.trim())
      errors.parent_address = t?.addressRequired || "Address is required";
    if (!formData.parent_district?.trim())
      errors.parent_district = t?.districtRequired || "District is required";
    if (!formData.parent_city?.trim())
      errors.parent_city = t?.talukRequired || "Taluk is required";
    if (!getVal(formData.parent_state))
      errors.parent_state = t?.stateRequired || "State is required";
    if (!formData.parent_pincode?.trim()) {
      errors.parent_pincode = t?.pincodeRequired || "Pincode is required";
    } else if (!isValidPincode(formData.parent_pincode)) {
      errors.parent_pincode =
        t?.pincodeInvalid || "Enter valid 6-digit pincode";
    }
  }

  if (livingWith === "guardian") {
    if (!formData.guardian_name?.trim())
      errors.guardian_name = t?.guardianNameRequired || "Name is required";
    if (!getVal(formData.guardian_occupation))
      errors.guardian_occupation =
        t?.occupationRequired || "Occupation is required";
    if (!formData.guardian_annual_salary?.toString().trim())
      errors.guardian_annual_salary =
        t?.incomeRequired || "Annual Income is required";
    if (!formData.guardian_address?.trim())
      errors.guardian_address = t?.addressRequired || "Address is required";
    if (!formData.guardian_district?.trim())
      errors.guardian_district = t?.districtRequired || "District is required";
    if (!formData.guardian_city?.trim())
      errors.guardian_city = t?.talukRequired || "Taluk is required";
    if (!getVal(formData.guardian_state))
      errors.guardian_state = t?.stateRequired || "State is required";
    if (!formData.guardian_pincode?.trim()) {
      errors.guardian_pincode = t?.pincodeRequired || "Pincode is required";
    } else if (!isValidPincode(formData.guardian_pincode)) {
      errors.guardian_pincode =
        t?.pincodeInvalid || "Enter valid 6-digit pincode";
    }
  }

  // ---- Academic Details ----
  if (!getVal(formData.qualified_eligibility_exam)) {
    errors.qualified_eligibility_exam =
      t?.qualifyingExamRequired || "Qualifying Examination is required";
  }

  if (!getVal(formData.exam_board_name)) {
    errors.exam_board_name =
      t?.examBoardRequired || "Examination Board is required";
  }

  // ---- Public Exam Marks ----
  if (!getVal(formData.qualified_exam_name)) {
    errors.qualified_exam_name =
      t?.qualifiedExamRequired || "Name of Examination Passed is required";
  }

  if (!formData.exam_month_and_year) {
    errors.exam_month_and_year =
      t?.examMonthYearRequired || "Month and Year is required";
  }

  if (!formData.register_number?.trim()) {
    errors.register_number =
      t?.registerNumberRequired || "Register Number is required";
  }

  const markFields = [
    { key: "tamil_mark", label: "TAMIL" },
    { key: "english_mark", label: "ENGLISH" },
    { key: "mathematics_mark", label: "MATHEMATICS" },
    { key: "science_mark", label: "SCIENCE" },
    { key: "social_science_mark", label: "SOCIAL SCIENCE" },
  ];

  markFields.forEach(({ key, label }) => {
    if (
      formData[key] === "" ||
      formData[key] === null ||
      formData[key] === undefined
    ) {
      errors[key] = `${label} mark is required`;
    } else if (!isValidMark(formData[key])) {
      errors[key] = `${label} mark must be between 0 and 100`;
    }
  });

  // ---- Preferred Course Details ----
  if (!getVal(formData.preferred_study_course)) {
    errors.preferred_study_course =
      t?.preferredStudyCourseRequired ||
      "Course Preferred to Study is required";
  }

  if (!getVal(formData.admission_for_which_year)) {
    errors.admission_for_which_year =
      t?.yearOfAdmissionRequired || "Year of Admission is required";
  }

  if (!getVal(formData.preferred_group_1)) {
    errors.preferred_group_1 =
      t?.preferredGroup1Required || "Preferred Group 1 is required";
  }

  if (!getVal(formData.mother_tongue)) {
    errors.mother_tongue =
      t?.motherTongueRequired || "Mother Tongue is required";
  }

  if (!formData.last_medium_of_instruction) {
    errors.last_medium_of_instruction =
      t?.mediumLeavingSchoolRequired || "Medium of Instruction is required";
  }

  // ---- Last Studied Details ----
  if (!getVal(formData.last_studied_class)) {
    errors.last_studied_class =
      t?.lastStudiedClassRequired || "Last Studied Class is required";
  }

  if (!formData.studied_year) {
    errors.studied_year = t?.studiedYearRequired || "Year of Study is required";
  }

  if (!formData.last_studied_school_name?.trim()) {
    errors.last_studied_school_name =
      t?.lastStudiedSchoolRequired || "Last Studied School is required";
  }

  if (!formData.emis_id?.trim()) {
    errors.emis_id = t?.emisNoRequired || "EMIS No is required";
  } else if (!isValidEmisNo(formData.emis_id)) {
    errors.emis_id =
      t?.emisNoInvalid || "Enter a valid EMIS No (numbers only, max 10 digits)";
  }

  if (!formData.reason_for_discontinuing?.trim()) {
    errors.reason_for_discontinuing =
      t?.reasonForLeavingRequired || "Reason for leaving is required";
  }

  // ---- Other Details ----
  if (!formData.place?.trim()) {
    errors.place = t?.placeRequired || "Place is required";
  }

  if (!formData.student_image) {
    errors.student_image =
      t?.studentImageRequired || "Student Image is required";
  }

  return errors;
};

// ==================== OTP VALIDATION ====================
export const validateOtp = (otp, translations) => {
  const t = translations?.labels || {};
  if (!otp?.trim()) return t?.otpRequired || "OTP is required";
  if (otp.length !== 6 || !/^\d{6}$/.test(otp))
    return t?.otpInvalid || "Enter a valid 6-digit OTP";
  return "";
};

export default validateHSCAdmissionForm;
